import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Paper, Toolbar, Typography, Button, ListItemIcon, ListItemText, List, ListItem, Chip, Dialog, DialogTitle, DialogContent } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import * as projectService from '../../services/projectService';
import { Inquiry, Package } from '../../services/projectService';
import MobileInquiryForm from './MobileInquiryForm';
import api from '../../services/api';
import { useNotification } from '../layouts/Layout';

interface MobileInquiriesListProps {
  projectId: string;
  projectName: string;
}

const MobileInquiriesList: React.FC<MobileInquiriesListProps> = ({ projectId, projectName }) => {
  const { showNotification } = useNotification();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuInquiryId, setMenuInquiryId] = useState<string | null>(null);
  const [openAddForm, setOpenAddForm] = useState(false);
  const [okOpen, setOkOpen] = useState(false);
  const [okTitle, setOkTitle] = useState<string>('');
  const [okSubtitle, setOkSubtitle] = useState<string>('');

  const reload = async () => {
    try {
      setLoading(true);
      const [inq, pkgs] = await Promise.all([
        projectService.getInquiriesByProjectId(projectId),
        projectService.getPackagesByProjectId(projectId)
      ]);
      setInquiries(inq || []);
      setPackages(pkgs || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Nie udało się pobrać zapytań/pakietów:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [projectId]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, inquiryId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuInquiryId(inquiryId);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuInquiryId(null);
  };

  const handleDelete = async () => {
    if (!menuInquiryId) return;
    try {
      await projectService.deleteInquiry(menuInquiryId);
      setInquiries(prev => prev.filter(i => i.inquiry_id !== menuInquiryId));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Nie udało się usunąć zapytania', e);
    } finally {
      handleCloseMenu();
    }
  };

  const handleCreateDraft = async () => {
    if (!menuInquiryId) return;
    try {
      const res = await api.post(`/inquiries/${menuInquiryId}/create-gmail-draft`);
      if (res.data?.success) {
        const draftId = res.data?.data?.draftId;
        setInquiries(prev => prev.map(i => i.inquiry_id === menuInquiryId ? { ...i, status: 'draft gmail' as any, gmail_draft_id: draftId } : i));
        showNotification('Utworzono draft w Gmail', 'success');
        setOkTitle('Utworzono draft');
        setOkSubtitle(`#${menuInquiryId}`);
        setOkOpen(true);
        setTimeout(()=> setOkOpen(false), 900);
      } else {
        showNotification(res.data?.message || 'Nie udało się utworzyć draftu', 'error');
      }
    } catch (e: any) {
      console.error(e);
      showNotification(e?.response?.data?.message || 'Błąd tworzenia draftu', 'error');
    } finally {
      handleCloseMenu();
    }
  };

  const handleSendGmail = async () => {
    if (!menuInquiryId) return;
    try {
      const inq = inquiries.find(i => i.inquiry_id === menuInquiryId);
      if (!inq?.gmail_draft_id) {
        showNotification('Brak draftu Gmail dla tego zapytania', 'warning');
        return;
      }
      const res = await api.post(`/inquiries/${menuInquiryId}/send-gmail-draft`, { draftId: inq.gmail_draft_id });
      if (res.data?.success) {
        const messageId = res.data?.data?.messageId;
        setInquiries(prev => prev.map(i => i.inquiry_id === menuInquiryId ? { ...i, status: 'Wysłane' as any, gmail_message_id: messageId || i.gmail_message_id } : i));
        showNotification('Wiadomość wysłana', 'success');
        setOkTitle('Wysłano');
        setOkSubtitle(`#${menuInquiryId}`);
        setOkOpen(true);
        setTimeout(()=> setOkOpen(false), 900);
      } else {
        showNotification(res.data?.message || 'Nie udało się wysłać wiadomości', 'error');
      }
    } catch (e: any) {
      console.error(e);
      showNotification(e?.response?.data?.message || 'Błąd wysyłania Gmail', 'error');
    } finally {
      handleCloseMenu();
    }
  };

  const handleSendDirect = async () => {
    if (!menuInquiryId) return;
    try {
      const res = await api.post(`/inquiries/${menuInquiryId}/send-direct-email`);
      if (res.data?.success) {
        const messageId = res.data?.data?.messageId;
        setInquiries(prev => prev.map(i => i.inquiry_id === menuInquiryId ? { ...i, status: 'Wysłane' as any, gmail_message_id: messageId || i.gmail_message_id } : i));
        showNotification('Wiadomość wysłana', 'success');
        setOkTitle('Wysłano');
        setOkSubtitle(`#${menuInquiryId}`);
        setOkOpen(true);
        setTimeout(()=> setOkOpen(false), 900);
      } else {
        showNotification(res.data?.message || 'Nie udało się wysłać wiadomości', 'error');
      }
    } catch (e: any) {
      console.error(e);
      showNotification(e?.response?.data?.message || 'Błąd wysyłania Gmail', 'error');
    } finally {
      handleCloseMenu();
    }
  };

  const handleAdd = async () => { await reload(); };

  const rows = useMemo(() => inquiries, [inquiries]);

  return (
    <Box>
      <Toolbar disableGutters sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>Zapytania</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAddForm(true)} sx={{ textTransform: 'none' }}>
          Utwórz
        </Button>
      </Toolbar>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <List disablePadding>
          {rows.map((inq) => (
            <ListItem key={inq.inquiry_id}
              secondaryAction={
                <IconButton edge="end" aria-label="menu" onClick={(e)=>handleOpenMenu(e, inq.inquiry_id)}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText
                primary={`${inq.inquiry_id} — ${inq.package_name || ''}`}
                primaryTypographyProps={{ noWrap: true }}
                secondary={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {inq.contractor_name || inq.contractor_email || '—'}
                    </Box>
                    {(() => {
                      const s = (inq.status || '').toLowerCase();
                      const config = s === 'wysłane'
                        ? { bg: '#E8F5E9', color: '#2E7D32', label: 'Wysłane' }
                        : s.includes('draft')
                          ? { bg: '#FFF8E1', color: '#FF8F00', label: 'Draft' }
                          : { bg: '#E3F2FD', color: '#1976D2', label: 'Przygotowane' };
                      return <Chip size="small" label={config.label} sx={{ bgcolor: config.bg, color: config.color, fontWeight: 600 }} />;
                    })()}
                  </Box>
                )}
              />
            </ListItem>
          ))}
          {!loading && rows.length === 0 && (
            <ListItem>
              <ListItemText primary="Brak zapytań" />
            </ListItem>
          )}
        </List>
      </Paper>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        {/* Draft Gmail dla statusu Przygotowane */}
        {menuInquiryId && (inquiries.find(i=>i.inquiry_id===menuInquiryId)?.status?.toLowerCase() === 'przygotowane') && (
          <MenuItem onClick={handleCreateDraft}>
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Draft (Gmail)</ListItemText>
          </MenuItem>
        )}
        {/* Wyślij bezpośrednio dla statusu Przygotowane */}
        {menuInquiryId && (inquiries.find(i=>i.inquiry_id===menuInquiryId)?.status?.toLowerCase() === 'przygotowane') && (
          <MenuItem onClick={handleSendDirect}>
            <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Wyślij (Gmail)</ListItemText>
          </MenuItem>
        )}
        {/* Wyślij Gmail dla statusu draft gmail */}
        {menuInquiryId && (inquiries.find(i=>i.inquiry_id===menuInquiryId)?.status?.toLowerCase() === 'draft gmail') && (
          <MenuItem onClick={handleSendGmail}>
            <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Wyślij (Gmail)</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <MobileInquiryForm
        open={openAddForm}
        onClose={() => { setOpenAddForm(false); reload(); }}
        projectId={projectId}
        projectName={projectName}
        packages={packages}
      />

      {/* Okejka po operacjach (spójna z Pakiety) */}
      <Dialog open={okOpen} onClose={()=>setOkOpen(false)} fullWidth maxWidth="xs" sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }} PaperProps={{ sx: { borderRadius: 3, mt: 'calc(88px + env(safe-area-inset-top))' } }}>
        <DialogTitle sx={{ textAlign:'center', fontWeight:700 }}>{okTitle}</DialogTitle>
        <DialogContent>
          <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', py: 4 }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
            {okSubtitle && (<Typography variant="body2" color="text.secondary">{okSubtitle}</Typography>)}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MobileInquiriesList;


